import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, finalize } from 'rxjs';
import { AuthService } from '../../../../common/auth/auth.service';
import { UserProfile } from '../../../../common/auth/auth.models';
import { BillingApiService, Product } from '../../../paywall/billing-api.service';
import { ToastService } from '../../../../common/services/toast.service';
import { AmplitudeService } from '../../../../common/analytics/amplitude.service';

@Injectable({
  providedIn: 'root',
})
export class DashboardSubscriptionService {
  private readonly authService = inject(AuthService);
  private readonly billingApiService = inject(BillingApiService);
  private readonly toastService = inject(ToastService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);

  // User profile
  private readonly userProfile$$ = new BehaviorSubject<UserProfile | null>(null);
  readonly userProfile$ = this.userProfile$$.asObservable();

  // Pricing modal
  private readonly isPricingModalOpen$$ = new BehaviorSubject<boolean>(false);
  readonly isPricingModalOpen$ = this.isPricingModalOpen$$.asObservable();

  // Subscription success modal
  private readonly isSubscriptionSuccessOpen$$ = new BehaviorSubject<boolean>(false);
  readonly isSubscriptionSuccessOpen$ = this.isSubscriptionSuccessOpen$$.asObservable();

  private readonly subscribedProductName$$ = new BehaviorSubject<string>('Premium');
  readonly subscribedProductName$ = this.subscribedProductName$$.asObservable();

  // Subscription info modal
  private readonly isSubscriptionInfoOpen$$ = new BehaviorSubject<boolean>(false);
  readonly isSubscriptionInfoOpen$ = this.isSubscriptionInfoOpen$$.asObservable();

  // Cancelling subscription state
  private readonly isCancellingSubscription$$ = new BehaviorSubject<boolean>(false);
  readonly isCancellingSubscription$ = this.isCancellingSubscription$$.asObservable();

  // Cancellation success state
  private readonly cancellationSuccess$$ = new BehaviorSubject<boolean>(false);
  readonly cancellationSuccess$ = this.cancellationSuccess$$.asObservable();

  loadUserProfile(): void {
    this.authService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => this.userProfile$$.next(profile),
        error: (err) => console.error('Failed to load user profile:', err),
      });
  }

  openPricingModal(): void {
    this.isPricingModalOpen$$.next(true);
  }

  closePricingModal(): void {
    this.isPricingModalOpen$$.next(false);
  }

  onPaymentSuccess(product: Product): void {
    this.isPricingModalOpen$$.next(false);
    this.subscribedProductName$$.next(product.name);
    this.isSubscriptionSuccessOpen$$.next(true);
    // Refresh user profile after a delay to allow webhook processing
    setTimeout(() => this.loadUserProfile(), 2000);
  }

  closeSubscriptionSuccess(): void {
    this.isSubscriptionSuccessOpen$$.next(false);
    // Refresh profile again when modal closes
    this.loadUserProfile();
  }

  openSubscriptionInfo(): void {
    this.isSubscriptionInfoOpen$$.next(true);
  }

  closeSubscriptionInfo(): void {
    this.isSubscriptionInfoOpen$$.next(false);
    this.cancellationSuccess$$.next(false);
  }

  onSubscriptionsClick(): void {
    const user = this.userProfile$$.value;
    const subscriptionStatus = user?.subscription?.status;

    // Check if user has an active or trialing subscription
    const hasPremium = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';

    if (hasPremium) {
      this.openSubscriptionInfo();
    } else {
      this.openPricingModal();
    }
  }

  cancelSubscription(): void {
    this.isCancellingSubscription$$.next(true);

    this.billingApiService
      .cancelSubscription()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isCancellingSubscription$$.next(false)),
      )
      .subscribe({
        next: () => {
          this.cancellationSuccess$$.next(true);
        },
        error: (err) => {
          console.error('Failed to cancel subscription:', err);
          const errorMessage =
            err?.error?.message || 'Failed to cancel subscription. Please try again.';
          this.toastService.error(errorMessage);
        },
      });
  }

  onCancellationVerificationComplete(): void {
    this.amplitudeService.trackEvent('subscription_cancelled');
    this.closeSubscriptionInfo();
    this.toastService.success('Your subscription has been scheduled for cancellation.');
    this.loadUserProfile();
  }
}
